import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../Icon';
import EmptyState from '../EmptyState';
import ContentCard from '../ContentCard';
import { MetricCard } from '../MetricCards';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import { useNotifications } from '../NotificationProvider';
import { useProject } from '../../contexts/ProjectContext';
import { useRole } from '../../hooks/useRole';
import { useTeamData } from './useTeamData';
import { requestTeamRefresh, resetTeamUi, setTeamTab, useTeamUi } from './teamUiStore';
import { useHeaderSearch } from '../../hooks/useHeaderSearch';
import { restoreTeamMember, updateTeamMember } from '../../services/TeamService';
import { errorMessageOf } from '../../utils/apiErrors';
import { downloadText } from '../../utils/download';
import { toCsv } from '../../utils/csv';
import { formatDateTime } from '../../utils/formatDate';
import { entityPath } from '../../utils/entityRoutes';
import { ROLE_LABELS } from '../../utils/roles';
import type { UserRole } from '../../types/User';
import type { LoginUser, TeamMember } from '../../types/Team';
import {
  RELATIONSHIP_LABELS, TEAM_CSV_HEADERS, filterTeam, loginFor, mergeLogins, teamCsvRows, teamMetrics,
} from '../../utils/teamView';
import { usePageRefresh } from '../../hooks/usePageRefresh';
import '../../styles/components/TeamView.css';
import { usePublishRecordList } from '../../hooks/useRecordList';

const SIDE_COLORS: Record<string, string> = {
  'bride-side': '#ad1457', 'groom-side': '#1565c0', vendor: '#2e7d32', neutral: '#6b7280',
};

const roleLabel = (role: string): string => ROLE_LABELS[role as UserRole] ?? role;

/**
 * Team: the people on this project (cards) and, on a second tab, the logins of
 * the workspace paired with their team profile. Staff e-mails and phones are
 * only served to admins and owners, so the page is too. The filters, New,
 * Export and Refresh live in the breadcrumb bar (see team/TeamBar) and reach
 * this view through the shared teamUiStore. Role changes stay in Settings
 * (owner only).
 */
const TeamView: React.FC = () => {
  const { isAdmin, isOwner } = useRole();
  const { activeProjectId } = useProject();
  const { showError, showSuccess } = useNotifications();
  const navigate = useNavigate();
  const ui = useTeamUi();
  const data = useTeamData(activeProjectId, ui.lifecycle, isAdmin);
  const [busyId, setBusyId] = useState<string | null>(null);

  // The list, side and tab choices belong to one visit to one project.
  useEffect(() => () => resetTeamUi(), [activeProjectId]);

  // Searching uses the page header's search box, like the other list pages.
  const search = useHeaderSearch();
  const visible = useMemo(() => filterTeam(data.shown, { ...ui.filters, search }), [data.shown, ui.filters, search]);
  usePublishRecordList('teamMember', visible.map(m => m.id));
  const metrics = useMemo(() => teamMetrics(data.active, data.logins), [data.active, data.logins]);
  const loginRows = useMemo(() => mergeLogins(data.logins, data.active), [data.logins, data.active]);

  // One-shot requests from the breadcrumb bar. Nonces seen at mount are ignored
  // so an old click does not replay when the screen opens.
  const seen = useRef({ exp: ui.exportNonce, ref: ui.refreshNonce });
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  useEffect(() => {
    if (ui.exportNonce !== seen.current.exp) {
      seen.current.exp = ui.exportNonce;
      downloadText('team-export.csv', toCsv(TEAM_CSV_HEADERS, teamCsvRows(visibleRef.current)));
      showSuccess('Team exported');
    }
    if (ui.refreshNonce !== seen.current.ref) {
      seen.current.ref = ui.refreshNonce;
      void data.refresh();
    }
  }, [ui.exportNonce, ui.refreshNonce, data, showSuccess]);

  // The header's Refresh asks this view through the store, like Export did.
  usePageRefresh(requestTeamRefresh);

  if (!isAdmin) {
    return (
      <div className="wt-container fade-in">
        <EmptyState icon="lock" title="Admin access needed" description="The Team page lists staff contact details, so it is open to admins and owners. Ask an owner if you need access." />
      </div>
    );
  }

  const open = (m: TeamMember) => {
    const path = entityPath('teamMember', m.id);
    if (path) navigate(path);
  };

  const run = async (member: TeamMember, action: () => Promise<unknown>, done: string) => {
    setBusyId(member.id);
    try {
      await action();
    } catch (err) {
      showError(errorMessageOf(err, 'That did not go through. Please try again.'));
      setBusyId(null);
      return;
    }
    // The action went through; a failed reload is shown by the list itself, never as a failed action.
    showSuccess(done);
    await data.refresh();
    setBusyId(null);
  };

  const hasPeople = data.shown.length > 0;
  const emptyTitle = ui.lifecycle === 'archived' ? 'No archived team members' : ui.lifecycle === 'trash' ? 'The trash is empty' : 'No team members yet';

  return (
    <div className="wt-container fade-in team-view">
      <section className="stats-grid" aria-label="Team summary">
        <MetricCard label="Team members" value={metrics.total} tone="blue" icon="users" />
        <MetricCard label="Active" value={metrics.active} tone="green" icon="check" />
        <MetricCard label="Deactivated" value={metrics.deactivated} tone="orange" icon="clock" />
        <MetricCard label="With a login" value={metrics.withLogin} tone="purple" icon="user" />
      </section>

      <div className="toolbar-tabs team-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={ui.tab === 'members'} className={ui.tab === 'members' ? 'active' : ''} onClick={() => setTeamTab('members')}>
          Team members
        </button>
        <button type="button" role="tab" aria-selected={ui.tab === 'logins'} className={ui.tab === 'logins' ? 'active' : ''} onClick={() => setTeamTab('logins')}>
          Logins
        </button>
      </div>

      {ui.tab === 'members' && (
        <>
          {data.loadError != null ? (
            <LoadErrorBox what="the team" error={data.loadError} onRetry={() => void data.refresh()} retrying={data.loading} />
          ) : data.loading && !hasPeople ? (
            <p className="team-loading" role="status">Loading the team…</p>
          ) : !hasPeople ? (
            <EmptyState
              icon="users"
              title={emptyTitle}
              description={ui.lifecycle === 'active' ? 'Add the people who help run this project.' : undefined}
              action={ui.lifecycle === 'active' ? (
                <Button variant="primary" icon={<Icon name="plus" size={14} />} onClick={() => navigate('/team-member/new')}>New member</Button>
              ) : undefined}
            />
          ) : visible.length === 0 ? (
            <EmptyState compact icon="users" title="No members match your search" description="Clear the header search or choose other options in Filters." />
          ) : (
            <div className="vendor-cards-grid">
              {visible.map(member => {
                const login = loginFor(member, data.logins);
                const side = member.relationship && member.relationship !== 'neutral' ? member.relationship : null;
                const away = ui.lifecycle !== 'active';
                const deactivated = member.is_active === false;
                return (
                  <ContentCard
                    key={member.id}
                    hideImage
                    title={member.name}
                    subtitle={member.role || undefined}
                    tags={[
                      ...(side ? [{ label: RELATIONSHIP_LABELS[side], color: SIDE_COLORS[side] }] : []),
                      ...(login ? [{ label: `Login: ${roleLabel(login.role)}`, color: undefined }] : []),
                      ...(member.teams ?? []).map(team => ({ label: team, color: undefined })),
                    ]}
                    details={[
                      ...(member.phone ? [{ label: 'Phone', value: member.phone }] : []),
                      ...(member.email ? [{ label: 'Email', value: member.email }] : []),
                      ...(member.last_login_at ? [{ label: 'Last login', value: formatDateTime(member.last_login_at) }] : []),
                    ]}
                    className={deactivated ? 'deactivated' : ''}
                    onClick={away ? undefined : () => open(member)}
                    actions={away ? [{
                      icon: 'refresh', title: busyId === member.id ? 'Restoring…' : 'Restore',
                      onClick: () => { if (busyId !== member.id) void run(member, () => restoreTeamMember(member.id), `${member.name} restored`); },
                    }] : deactivated ? [{
                      icon: 'refresh', title: busyId === member.id ? 'Reactivating…' : 'Reactivate',
                      onClick: () => { if (busyId !== member.id) void run(member, () => updateTeamMember(member.id, { is_active: true }), `${member.name} reactivated`); },
                    }] : undefined}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      {ui.tab === 'logins' && (
        <LoginsTab
          rows={loginRows}
          error={data.loginsError}
          loading={data.loading}
          isOwner={isOwner}
          onRetry={() => void data.refresh()}
          onOpen={open}
          onManage={() => navigate('/settings/workspace')}
          onAddProfile={(login) => navigate(`/team-member/new?email=${encodeURIComponent(login.email ?? '')}&name=${encodeURIComponent(login.name ?? '')}`)}
        />
      )}
    </div>
  );
};

interface LoginsTabProps {
  rows: ReturnType<typeof mergeLogins>;
  error: unknown;
  loading: boolean;
  isOwner: boolean;
  onRetry: () => void;
  onOpen: (m: TeamMember) => void;
  onManage: () => void;
  onAddProfile: (login: LoginUser) => void;
}

const LoginsTab: React.FC<LoginsTabProps> = ({ rows, error, loading, isOwner, onRetry, onOpen, onManage, onAddProfile }) => {
  if (error != null) return <LoadErrorBox what="the logins" error={error} onRetry={onRetry} retrying={loading} />;
  if (loading && rows.length === 0) return <p className="team-loading" role="status">Loading logins…</p>;
  if (rows.length === 0) return <EmptyState compact icon="user" title="No logins yet" description="People you invite in Settings appear here." />;
  return (
    <div className="team-logins">
      <p className="team-logins__hint">
        Everyone who can sign in to this workspace, with the team profile that shares their e-mail.
        {isOwner ? ' Only an owner can invite people or change a role. To add someone to the team of another project, use Team and contacts in Workspace settings.' : ' Roles are changed by an owner in Settings.'}
      </p>
      {isOwner && <Button variant="secondary" size="sm" icon={<Icon name="settings" size={14} />} onClick={onManage}>Manage logins</Button>}
      <table className="team-logins__table">
        <thead>
          <tr><th>Name</th><th>E-mail</th><th>Role</th><th>Status</th><th>Last login</th><th>Team profile</th></tr>
        </thead>
        <tbody>
          {rows.map(({ login, member }) => (
            <tr key={login.id}>
              <td>{login.name || '—'}</td>
              <td>{login.email || 'No e-mail on file'}</td>
              <td><span className="ds-badge ds-badge-neutral">{roleLabel(login.role)}</span></td>
              <td>{login.status}</td>
              <td>{login.last_login_at ? formatDateTime(login.last_login_at) : 'Never'}</td>
              <td>
                {member ? (
                  <button type="button" className="team-logins__link" onClick={() => onOpen(member)}>{member.name}</button>
                ) : login.email ? (
                  <button type="button" className="team-logins__link" onClick={() => onAddProfile(login)}>Add team profile</button>
                ) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TeamView;
